import {
  isGroup,
  type Filter,
  type FilterCondition,
  type FilterGroup,
  type FilterNode,
  type WarehouseStockCell,
} from "@estelle/domain";

/**
 * What a cell and a total count in Stock Levels' By warehouse layout (E1,
 * #441): what can be sold, or everything physically there.
 *
 * On hand is available plus committed — stock reserved for an order that has
 * not shipped is still on the shelf. In transit is deliberately in neither
 * (#420).
 */
export type Measure = "available" | "onHand";

/** The figure a cell or a total shows. A null `committed` adds nothing. */
export function quantity(
  figures: Pick<WarehouseStockCell, "available" | "committed">,
  measure: Measure,
): number {
  return measure === "available"
    ? figures.available
    : figures.available + (figures.committed ?? 0);
}

/** `INVENTORY_FIELDS`' warehouse field, whose value is the warehouse's code. */
const WAREHOUSE_FIELD = "warehouse";

/**
 * Which warehouse the filter restricts Stock Levels to, as far as one
 * segmented control can say it.
 *
 * `custom` is a warehouse condition the switcher cannot express — two
 * warehouses, a negation, one inside an OR — which is shown as such rather
 * than as "All" over a filter that is not.
 */
export type WarehouseSelection =
  { kind: "all" } | { kind: "one"; code: string } | { kind: "custom" };

function warehouseConditions(node: FilterNode): FilterCondition[] {
  if (isGroup(node)) return node.children.flatMap(warehouseConditions);
  return node.field === WAREHOUSE_FIELD ? [node] : [];
}

/** The code a condition names when it names exactly one: `is X` or `is any of [X]`. */
function singleCode(condition: FilterCondition): string | null {
  const { operator, value } = condition;
  if (operator === "is" && typeof value === "string" && value !== "") {
    return value;
  }
  if (
    operator === "is_any_of" &&
    Array.isArray(value) &&
    value.length === 1 &&
    typeof value[0] === "string" &&
    value[0] !== ""
  ) {
    return value[0];
  }
  return null;
}

export function warehouseSelection(filter: Filter): WarehouseSelection {
  const found = warehouseConditions(filter);
  const only = found[0];
  if (!only) return { kind: "all" };

  // One condition, ANDed in at the top: the only shape the switcher writes
  // and the only one it can claim to represent.
  if (
    found.length === 1 &&
    filter.combinator === "and" &&
    filter.children.includes(only)
  ) {
    const code = singleCode(only);
    if (code !== null) return { kind: "one", code };
  }
  return { kind: "custom" };
}

function withoutWarehouse(group: FilterGroup): FilterGroup {
  const children = group.children.flatMap((child): FilterNode[] => {
    if (isGroup(child)) {
      const pruned = withoutWarehouse(child);
      return pruned.children.length > 0 ? [pruned] : [];
    }
    return child.field === WAREHOUSE_FIELD ? [] : [child];
  });
  return { ...group, children };
}

/**
 * The filter with its warehouse restriction replaced by `code`, or removed
 * for `null` (All) — the condition the filter builder would write for
 * "Warehouse is X", so the chip, the URL, saved views and the API see nothing
 * unusual about it.
 *
 * Replaces every warehouse condition wherever it sits, including the custom
 * ones the switcher cannot show: choosing a segment is choosing, and leaving
 * a second one behind would quietly intersect with it. Everything else in the
 * filter is untouched. An OR at the root cannot take a further ANDed
 * condition without changing what it says, so it is wrapped instead.
 */
export function selectWarehouse(filter: Filter, code: string | null): Filter {
  const rest = withoutWarehouse(filter);
  if (code === null) return rest;

  const condition: FilterCondition = {
    kind: "condition",
    field: WAREHOUSE_FIELD,
    operator: "is",
    value: code,
  };
  if (rest.combinator === "and") {
    return { ...rest, children: [...rest.children, condition] };
  }
  return {
    kind: "group",
    combinator: "and",
    children: [...(rest.children.length > 0 ? [rest] : []), condition],
  };
}
